import { useEffect, useState } from "react";
import { authService } from "../../services/api";
import { useNotification } from "../Notifications/NotificationContext";

function formatWhen(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
}

export default function Sessions() {
    const { showError, showSuccess } = useNotification();

    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [revokingId, setRevokingId] = useState(null);
    const [showAll, setShowAll] = useState(false);

    const VISIBLE = 2;

    // Carrega uma única vez ao montar. showError NÃO entra nas deps de
    // propósito: sua identidade muda a cada render e disparava um loop de GETs.
    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            try {
                const { data } = await authService.list_sessions();
                const list = Array.isArray(data?.sessions) ? data.sessions : [];
                // "Atual" sempre no topo; o resto mantém a ordem do backend.
                list.sort((a, b) => (b.current ? 1 : 0) - (a.current ? 1 : 0));
                if (alive) setSessions(list);
            } catch (err) {
                console.warn("[Sessions] list error:", err?.response?.data || err?.message);
                if (alive) showError("Erro ao carregar sessões.");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRevoke = async (session) => {
        if (session.current) return;
        setRevokingId(session.id);
        try {
            await authService.revoke_session(session.id);
            setSessions((prev) => prev.filter((s) => s.id !== session.id));
            showSuccess("Sessão encerrada com sucesso.");
        } catch (err) {
            console.warn("[Sessions] revoke error:", err?.response?.data || err?.message);
            showError("Erro ao encerrar sessão. " + (err?.response?.data?.error || ""));
        } finally {
            setRevokingId(null);
        }
    };

    return (
        <div className="ob-card mt-4 mb-3">
            <div className="p-4">
                <div className="flex items-center gap-2 mb-3">
                    <i className="fas fa-shield-halved text-[var(--purple)] text-sm" aria-hidden="true" />
                    <h3 className="font-semibold text-white text-sm">
                        Sessões {!loading && `(${sessions.length})`}
                    </h3>
                </div>

                {loading ? (
                    <p className="text-[11px] text-white/40">
                        <i className="fas fa-spinner fa-spin me-1" /> Carregando sessões...
                    </p>
                ) : sessions.length === 0 ? (
                    <p className="text-[11px] text-white/40">Nenhuma sessão ativa.</p>
                ) : (
                    <div className="flex flex-col gap-2">
                        {(showAll ? sessions : sessions.slice(0, VISIBLE)).map((s) => (
                            <div
                                key={s.id}
                                className="p-3 rounded-xl bg-white/[0.02] border border-white/10 flex items-center justify-between gap-3"
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-9 h-9 rounded-lg flex items-center justify-center border bg-white/[0.03] border-white/10 text-white/60 shrink-0">
                                        <i className={`fas ${s.is_mobile ? "fa-mobile-screen" : "fa-desktop"}`} />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <h4 className="text-xs font-bold text-white truncate">
                                                {s.os} · {s.browser}
                                            </h4>
                                            {s.current && (
                                                <span className="px-2 py-0.5 rounded-md text-[8px] font-bold uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                                    Atual
                                                </span>
                                            )}
                                        </div>
                                        {s.ip_address && (
                                            <p className="text-[9px] text-white/40 truncate mt-0.5">
                                                <i className="fas fa-map-marker-alt me-1" />
                                                {s.ip_address}
                                            </p>
                                        )}
                                        <p className="text-[9px] text-white/30 mt-0.5">
                                            Visto em {formatWhen(s.last_used_at)}
                                        </p>
                                    </div>
                                </div>

                                {!s.current && (
                                    <button
                                        type="button"
                                        onClick={() => handleRevoke(s)}
                                        disabled={revokingId === s.id}
                                        className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold text-red-400 border border-red-500/20 bg-red-500/[0.06] hover:bg-red-500/[0.12] transition-colors cursor-pointer disabled:opacity-50"
                                    >
                                        <i className={`fas ${revokingId === s.id ? "fa-spinner fa-spin" : "fa-sign-out-alt"}`} />
                                        Encerrar
                                    </button>
                                )}
                            </div>
                        ))}

                        {sessions.length > VISIBLE && (
                            <button
                                type="button"
                                onClick={() => setShowAll((v) => !v)}
                                className="flex items-center justify-center gap-1.5 w-full py-2 rounded-xl text-[11px] font-semibold text-white/50 hover:text-white bg-white/[0.02] hover:bg-white/[0.05] border border-white/10 transition-colors cursor-pointer"
                            >
                                <i className={`fas fa-chevron-${showAll ? "up" : "down"}`} />
                                {showAll
                                    ? "Ver menos"
                                    : `Ver todas as sessões (${sessions.length - VISIBLE} ocultas)`}
                            </button>
                        )}
                    </div>
                )}

                <p className="text-[10px] text-amber-400/60 mt-2">
                    Sessões inativas por mais de 7 dias são removidas automaticamente.
                </p>
            </div>
        </div>
    );
}
